import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';
import { ApiAuthBridge } from '@/components/layout/ApiAuthBridge';
import { PaywallModal } from '@/components/paywall/PaywallModal';
import { AccountGateModal } from '@/components/account/AccountGateModal';
import { trackPageView } from '@/lib/analytics';
import { PageLoader } from '@/components/layout/PageLoader';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { ProtectedAdminRoute } from '@/components/layout/ProtectedAdminRoute';
import { LoginPage } from '@/pages/Login';
import { LogoutPage } from '@/pages/Logout';
import { CheckoutCompletePage } from '@/pages/CheckoutComplete';
import { InvitePage } from '@/pages/Invite';
import { NotFoundPage } from '@/pages/NotFound';
import { OnboardingPage } from '@/pages/Onboarding';
import { ReadersPage } from '@/pages/Readers';
import { ReaderEditPage } from '@/pages/ReaderEdit';
import { AccountPage } from '@/pages/Account';
import { LibraryPage } from '@/pages/Library';
import { LearnsPage } from '@/pages/Learns';
import { ArticlePage } from '@/pages/Article';
import { QuizPage } from '@/pages/Quiz';
import { ReaderBadgesPage } from '@/pages/ReaderBadges';
import { CatalogPage } from '@/pages/Catalog';
import { PricingPage } from '@/pages/Pricing';
import { ReadingListPage } from '@/pages/ReadingList';
import { ReadingTrackerPage } from '@/pages/ReadingTracker';
import { AdminDashboardPage } from '@/pages/admin/AdminDashboard';
import { AdminArticlesPage } from '@/pages/admin/AdminArticles';
import { AdminEditArticlePage } from '@/pages/admin/AdminEditArticle';
import { AdminEditBookPage } from '@/pages/admin/AdminEditBook';
import { AdminEditQuizPage } from '@/pages/admin/AdminEditQuiz';
import { AdminReviewsPage } from '@/pages/admin/AdminReviews';
import { AdminAffiliatesPage } from '@/pages/admin/AdminAffiliates';
import { AdminSubscribersPage } from '@/pages/admin/AdminSubscribers';
import { AdminSubscriberListsPage } from '@/pages/admin/AdminSubscriberLists';
import { AdminSubscriberListPage } from '@/pages/admin/AdminSubscriberList';
import { AdminEmailTemplatesPage } from '@/pages/admin/AdminEmailTemplates';
import { AdminEditEmailTemplatePage } from '@/pages/admin/AdminEditEmailTemplate';
import { AdminBroadcastsPage } from '@/pages/admin/AdminBroadcasts';
import { AdminCreateBroadcastPage } from '@/pages/admin/AdminCreateBroadcast';
import { AdminBroadcastDetailPage } from '@/pages/admin/AdminBroadcastDetail';
import { AdminAutomationsPage } from '@/pages/admin/AdminAutomations';
import { AdminCreateAutomationPage } from '@/pages/admin/AdminCreateAutomation';
import { AdminEditAutomationPage } from '@/pages/admin/AdminEditAutomation';
import { AdminMonitoringPage } from '@/pages/admin/AdminMonitoring';
import { RewardsPage } from '@/pages/Rewards';

/**
 * RootRedirect — handles the `/` route.
 *
 * Critical: when Auth0 redirects the browser back to
 * `http://localhost:5173/?code=…&state=…`, React mounts the entire tree and
 * React's effect ordering (children first, then ancestors) means a naive
 * `<Navigate to="/library" replace />` would call `history.replace`
 * *before* `Auth0Provider`'s effect calls `handleRedirectCallback()` — the
 * `code`/`state` params vanish from `window.location.search` and the SDK
 * can't exchange them for a token.
 *
 * We rely on `Auth0Provider` initialising with `isLoading: true` and only
 * flipping to `false` once `handleRedirectCallback` has settled (success or
 * error). That gives us:
 *   1. PageLoader paints while the SDK is processing the callback.
 *   2. On success, `Auth0ProviderWithNavigate.onRedirectCallback` owns the
 *      redirect to `/library` (or the original `returnTo`).
 *   3. On failure, `error` is populated; we surface a toast and bounce
 *      back to `/login` instead of deadlocking on a spinner.
 *
 * The previous implementation also gated on `window.location.search`
 * containing `code=&state=`, which created a deadlock when the SDK errored
 * during the exchange: `isLoading` would settle to false but the URL still
 * carried the params, so the loader stayed up forever.
 *
 * Guest browsing: unauthenticated visitors are no longer bounced to
 * `/login` — they land on `/library` and browse as a guest (login is opt-in
 * from the header or any account gate). Only a failed callback falls back to
 * `/login` so the error toast has somewhere to live.
 */
function RootRedirect() {
  const { isLoading, error } = useAuth0();

  useEffect(() => {
    if (!error) return;
    // eslint-disable-next-line no-console
    console.error('[auth] Auth0 redirect callback failed:', error);
    toast.error(`Sign-in failed: ${error.message}`);
  }, [error]);

  if (isLoading) {
    return <PageLoader label="Signing you in…" />;
  }
  if (error) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to="/library" replace />;
}

/**
 * Route table. Phase 1 ships the Login page and auth-gated shells; later
 * phases replace the Placeholder pages with real implementations as per
 * docs/CLIENT_PLAN.md.
 */
function AnalyticsRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

export function App() {
  return (
    <>
      <ApiAuthBridge />
      <PaywallModal />
      <AccountGateModal />
      <AnalyticsRouteTracker />
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        {/* Public sign-out route — reachable on any device (outside the auth
            + mobile gates). Clears stores, then hands off to Auth0 logout. */}
        <Route path="/logout" element={<LogoutPage />} />
        {/* Public post-checkout confirmation — the Stripe success_url for
            mobile-browser upgrades. Intentionally outside ProtectedRoute so
            it's reachable on a phone (no auth/account/mobile gate). */}
        <Route path="/checkout/complete" element={<CheckoutCompletePage />} />
        {/* Public referral landing page — the `/invite?ref=<code>` share link
            target. Must stay outside ProtectedRoute so logged-out invitees can
            see it; the ref is captured at boot in main.tsx. */}
        <Route path="/invite" element={<InvitePage />} />

        {/* Guest-accessible browse routes — readable without an account. The
            catalogue reads (GET /books, /labels) are public; account-based
            features inside Book Detail route through the AccountGateModal. */}
        <Route element={<ProtectedRoute guestAccessible />}>
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/catalog" element={<CatalogPage />} />
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/learns" element={<LearnsPage />} />
          <Route path="/learns/articles/:slug" element={<ArticlePage />} />
          <Route path="/quiz/:quizId" element={<QuizPage />} />
          <Route path="/readers/:id/badges" element={<ReaderBadgesPage />} />
          <Route path="/list" element={<ReadingListPage />} />
          <Route
            path="/reader/:readerId/book/:bookId/tracker"
            element={<ReadingTrackerPage />}
          />
          <Route path="/readers" element={<ReadersPage />} />
          <Route path="/readers/:id" element={<ReaderEditPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/rewards" element={<RewardsPage />} />

          <Route element={<ProtectedAdminRoute />}>
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/admin/books/:id" element={<AdminEditBookPage />} />
            <Route path="/admin/articles" element={<AdminArticlesPage />} />
            <Route path="/admin/articles/new" element={<AdminEditArticlePage />} />
            <Route path="/admin/articles/:id" element={<AdminEditArticlePage />} />
            <Route path="/admin/quizzes/new" element={<AdminEditQuizPage />} />
            <Route path="/admin/quizzes/:id" element={<AdminEditQuizPage />} />
            <Route path="/admin/reviews" element={<AdminReviewsPage />} />
            <Route path="/admin/affiliates" element={<AdminAffiliatesPage />} />
            <Route path="/admin/subscribers" element={<AdminSubscribersPage />} />
            <Route path="/admin/subscribers/lists" element={<AdminSubscriberListsPage />} />
            <Route path="/admin/subscribers/lists/:id" element={<AdminSubscriberListPage />} />
            <Route path="/admin/subscribers/templates" element={<AdminEmailTemplatesPage />} />
            <Route
              path="/admin/subscribers/templates/new"
              element={<AdminEditEmailTemplatePage />}
            />
            <Route path="/admin/subscribers/templates/:id" element={<AdminEditEmailTemplatePage />} />
            <Route path="/admin/subscribers/broadcasts" element={<AdminBroadcastsPage />} />
            <Route
              path="/admin/subscribers/broadcasts/new"
              element={<AdminCreateBroadcastPage />}
            />
            <Route
              path="/admin/subscribers/broadcasts/:id"
              element={<AdminBroadcastDetailPage />}
            />
            <Route path="/admin/subscribers/automations" element={<AdminAutomationsPage />} />
            <Route
              path="/admin/subscribers/automations/new"
              element={<AdminCreateAutomationPage />}
            />
            <Route
              path="/admin/subscribers/automations/:id"
              element={<AdminEditAutomationPage />}
            />
            <Route path="/admin/monitoring" element={<AdminMonitoringPage />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}
